import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import { Link as RouterLink } from "react-router-dom";
import type { ReactNode } from "react";
import type {
  CommunityGameNightResponse,
  CommunityResponse,
} from "@bw/shared/types/generated";
import { useAuth, useFeed } from "@bw/shared/hooks";
import { playerHandleSuffix } from "@bw/shared/components";
import { useCommunities, useCommunityMembership } from "../hooks";
import { useCommunityHubActivity } from "../hooks/useCommunityHubActivity";
import { CommunityAvatar } from "../components/CommunityAvatar";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { DateBadge } from "../../../common/components/DateBadge";
import { DenseRow } from "../../../common/components/DenseRow";
import { ListSkeleton } from "../components/ListSkeleton";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { useGuardianLookOnly } from "../../../common/query/useGuardianLink";
import { guardianDenialFromError } from "@bw/shared/api";

const communityPath = (id: string, view?: string) =>
  `/community/communities/${id}${view ? `?view=${view}` : ""}`;

function memberLabel(count: number | undefined): string {
  if (typeof count !== "number") return "— members";
  return `${count} ${count === 1 ? "member" : "members"}`;
}

function eventTime(event: CommunityGameNightResponse): string {
  const at = new Date(event.starts_at);
  try {
    return new Intl.DateTimeFormat(undefined, { timeZone: event.timezone, hour: "numeric", minute: "2-digit" }).format(at);
  } catch {
    // An IANA zone the browser's Intl doesn't know falls back to local time.
    return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(at);
  }
}

const SIGN_UP_LABEL: Record<string, string> = {
  going: "Signed up",
  waitlisted: "Waitlisted",
};

const twoLines = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  overflowWrap: "anywhere",
} as const;

const relativeTime = new Intl.RelativeTimeFormat(undefined, { numeric: "auto", style: "short" });
function ago(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 60) return relativeTime.format(-Math.max(minutes, 1), "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 24) return relativeTime.format(-hours, "hour");
  return relativeTime.format(-Math.round(hours / 24), "day");
}

function DiscoverCard({ community }: { community: CommunityResponse }) {
  const { join, request } = useCommunityMembership(community.community_id);
  // CommunityResponse carries no pending-request flag, so a confirmed request
  // is shown as "Requested" for this visit instead of offering it again.
  const requested = request?.isSuccess ?? false;
  // A guardian-control 403 is answered by the app-wide GuardianPrompt (this
  // card is too small for the notice), so it is not also "That didn't work".
  const failed =
    (join.isError && !guardianDenialFromError(join.error)) ||
    (request?.isError === true && !guardianDenialFromError(request.error));
  const action =
    community.join_mode === "open"
      ? { label: "Join", run: () => join.mutate(), pending: join.isPending }
      : community.join_mode === "request"
        ? {
            label: requested ? "Requested" : "Request",
            run: () => request?.mutate(),
            pending: (request?.isPending ?? false) || requested,
          }
        : null;
  return (
    <Paper
      component="li"
      variant="outlined"
      sx={{
        listStyle: "none",
        flex: "0 0 148px",
        scrollSnapAlign: "start",
        p: 1.5,
        borderRadius: 3,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Box
        component={RouterLink}
        to={communityPath(community.community_id)}
        sx={{
          color: "inherit",
          textDecoration: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          width: "100%",
          borderRadius: 2,
        }}
      >
        <CommunityAvatar communityId={community.community_id} name={community.name} size={48} />
        <Typography sx={{ ...twoLines, fontWeight: 600, fontSize: "0.875rem", maxWidth: "100%" }}>
          {community.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {memberLabel(community.member_count)}
        </Typography>
      </Box>
      {failed ? (
        <Typography role="alert" variant="caption" color="error">
          That didn&apos;t work. Try again.
        </Typography>
      ) : null}
      {action ? (
        <Button
          size="small"
          variant="contained"
          fullWidth
          disabled={action.pending}
          onClick={action.run}
          aria-label={`${action.label} ${community.name}`}
          sx={{ mt: 0.5 }}
        >
          {action.label}
        </Button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          fullWidth
          component={RouterLink}
          to={communityPath(community.community_id)}
          sx={{ mt: 0.5 }}
        >
          View
        </Button>
      )}
    </Paper>
  );
}

/**
 * Community hub (/community). One dense page instead of Discover/My tabs:
 * joined communities as an avatar row (ring = unread chat), upcoming events,
 * recent activity, then discovery as a swipe row. Membership, counts, RSVP and
 * unread state are rendered from server payloads only.
 */
export function SocialPage() {
  const { user } = useAuth();
  const { lookOnly, paused, known } = useGuardianLookOnly(Boolean(user));
  // A possible minor's controls are still loading: don't read the feed yet,
  // and don't guess between "waiting for approval" and "paused".
  if (!known) {
    return <ListSkeleton />;
  }
  if (lookOnly) {
    return <LookOnlyCommunity paused={paused} />;
  }
  return <CommunityHub />;
}

/**
 * Look-only mode (#2691): a minor with no guardian yet, or one a guardian
 * paused, sees why Community is closed instead of the feed and the hub.
 */
function LookOnlyCommunity({ paused }: { paused: boolean }) {
  return (
    <Stack spacing={2} sx={{ width: "100%", minWidth: 0, maxWidth: 720 }}>
      <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700 }}>
        Community
      </Typography>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
          <Typography sx={{ fontWeight: 700 }}>
            {paused ? "A guardian has paused your account" : "Ask a parent or guardian to approve your account"}
          </Typography>
          <Typography color="text.secondary">
            {paused
              ? "Communities, posts and messages come back when a guardian turns your account back on."
              : "Communities, posts and messages open once a parent or guardian approves your account. You can still browse clubs, schedules and tournaments."}
          </Typography>
          {paused ? null : (
            <Button component={RouterLink} to="/me/guardian" variant="contained" size="small">
              Ask a guardian
            </Button>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

function CommunityHub() {
  const { user } = useAuth();
  const communitiesQ = useCommunities();
  const all: CommunityResponse[] = communitiesQ.data ?? [];
  const joined = all.filter((c) => c.is_member);
  const discover = all.filter((c) => !c.is_member);
  const hub = useCommunityHubActivity(joined);
  const feedQ = useFeed({ scope: "communities", limit: 5 }, { enabled: Boolean(user) });
  const recentPosts = (feedQ.posts ?? []).filter((post) => post.community_id && !post.is_deleted).slice(0, 3);
  const hasActivity = hub.unreadChannels.length > 0 || recentPosts.length > 0;

  return (
    <Stack spacing={2.5} useFlexGap sx={{ width: "100%", minWidth: 0, maxWidth: 720 }}>
      <Typography component="h1" sx={visuallyHidden}>
        Community
      </Typography>

      {communitiesQ.isError ? (
        <Alert severity="error" role="alert">
          Couldn&apos;t load communities. Please try again.
        </Alert>
      ) : communitiesQ.isLoading ? (
        <ListSkeleton />
      ) : (
        <>
          <Stack component="section" aria-labelledby="hub-yours" spacing={1}>
            <SectionHeader id="hub-yours" title="Your communities" />
            {joined.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                You haven&apos;t joined any communities yet. Find one below.
              </Typography>
            ) : (
              <Stack
                component="ul"
                direction="row"
                spacing={1.5}
                sx={{ m: 0, p: 0, overflowX: "auto", scrollSnapType: "x mandatory", pb: 0.5 }}
              >
                {joined.map((community) => {
                  const unread = hub.unreadByCommunity.get(community.community_id) ?? 0;
                  return (
                    <Box
                      component="li"
                      key={community.community_id}
                      sx={{ listStyle: "none", flex: "0 0 72px", scrollSnapAlign: "start" }}
                    >
                      <RouterLink
                        to={communityPath(community.community_id)}
                        aria-label={`${community.name}${unread > 0 ? `, ${unread} unread` : ""}`}
                        style={{ color: "inherit", textDecoration: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
                      >
                        <CommunityAvatar communityId={community.community_id} name={community.name} size={56} ring={unread > 0} />
                        <Typography noWrap variant="caption" sx={{ maxWidth: 72 }}>
                          {community.name}
                        </Typography>
                      </RouterLink>
                    </Box>
                  );
                })}
                <Box component="li" sx={{ listStyle: "none", flex: "0 0 72px" }}>
                  <a
                    href="#hub-discover"
                    style={{ color: "inherit", textDecoration: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
                  >
                    <Box sx={{ p: "2px", border: 2, borderColor: "transparent", display: "inline-flex" }}>
                      <Avatar sx={{ width: 56, height: 56, bgcolor: "transparent", border: "2px dashed", borderColor: "divider", color: "text.secondary" }}>
                        <AddRounded />
                      </Avatar>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Find</Typography>
                  </a>
                </Box>
              </Stack>
            )}
          </Stack>

          {hub.upcoming.length > 0 ? (
            <Stack component="section" aria-labelledby="hub-next" spacing={0.5}>
              <SectionHeader id="hub-next" title="Up next" action={{ label: "Calendar", to: "/me/calendar" }} />
              <List disablePadding>
                {hub.upcoming.slice(0, 3).map(({ community, event }) => {
                  const status = event.my_rsvp_status ? SIGN_UP_LABEL[event.my_rsvp_status] ?? null : null;
                  return (
                    <DenseRow
                      key={event.event_id}
                      to={communityPath(community.community_id, "events")}
                      leading={<DateBadge at={event.starts_at} timeZone={event.timezone} />}
                      title={event.title}
                      subtitle={`${community.name} · ${event.location_label} · ${eventTime(event)}`}
                      trailing={
                        <Chip
                          size="small"
                          label={status ?? "Sign up"}
                          color={status === "Signed up" ? "primary" : "default"}
                          variant={status ? "filled" : "outlined"}
                        />
                      }
                    />
                  );
                })}
              </List>
            </Stack>
          ) : null}

          {hasActivity ? (
            <Stack component="section" aria-labelledby="hub-activity" spacing={0.5}>
              <SectionHeader id="hub-activity" title="Activity" />
              <List disablePadding>
                {hub.unreadChannels.slice(0, 3).map(({ community, channel }) => (
                  <DenseRow
                    key={channel.conversation_id}
                    to={`/community/communities/${community.community_id}/channels/${channel.conversation_id}`}
                    leading={
                      <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: "action.hover", color: "text.secondary" }}>
                        <ForumRounded fontSize="small" />
                      </Avatar>
                    }
                    title={`# ${channel.name}`}
                    subtitle={community.name}
                    trailing={<Chip size="small" color="primary" label={`${channel.unread_count} new`} />}
                  />
                ))}
                {recentPosts.map((post) => (
                  <DenseRow
                    key={post.post_id}
                    to={`/community/posts/${post.post_id}`}
                    leading={
                      <Avatar aria-hidden src={post.author_avatar_url ?? undefined} sx={{ width: 40, height: 40 }}>
                        {(post.author_display_name ?? "?").slice(0, 1).toUpperCase()}
                      </Avatar>
                    }
                    title={
                      <>
                        {post.author_display_name ?? "A member"}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {` ${playerHandleSuffix(post.author_id)}`}
                        </Typography>
                        <Typography component="span" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {" "}in {post.community_name ?? "a community"}
                        </Typography>
                      </>
                    }
                    subtitle={`${post.content || "Shared a post"} · ${ago(post.created_at)}`}
                  />
                ))}
              </List>
            </Stack>
          ) : null}

          {hub.failed ? (
            <Typography role="status" variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
              Some community updates couldn&apos;t load right now.
            </Typography>
          ) : null}

          <Stack component="section" aria-labelledby="hub-discover" spacing={1}>
            <SectionHeader id="hub-discover" title="Discover communities" />
            {discover.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                You&apos;re in every community available to you.
              </Typography>
            ) : (
              <Stack
                component="ul"
                direction="row"
                spacing={1.25}
                sx={{ m: 0, p: 0, overflowX: "auto", scrollSnapType: "x mandatory", pb: 1 }}
              >
                {discover.map((community) => (
                  <DiscoverCard key={community.community_id} community={community} />
                ))}
              </Stack>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}
